import { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';
import { useStore } from '../../store';
import type { ChartConfig } from '../../types/visualizations';

interface ChartBlockProps {
  config: ChartConfig;
}

// Default color palette
const COLORS = [
  '#3b4e6d', // Primary accent
  '#5a7faa',
  '#7ba3c9',
  '#9cc5e4',
  '#bde4ff',
  '#6b8e7e',
  '#8fb3a3',
  '#b3d6c8',
  '#d4b896',
  '#e8cdb0',
];

export default function ChartBlock({ config }: ChartBlockProps) {
  const { parsedDatasets } = useStore();

  const dataset = useMemo(() => {
    return parsedDatasets.find((d) => d.id === config.datasetId);
  }, [parsedDatasets, config.datasetId]);

  const chartData = useMemo(() => {
    if (!dataset) return [];
    return dataset.rows;
  }, [dataset]);

  const colors = config.colors || COLORS;

  if (!dataset || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <p className="text-sm text-gray-400">No data available</p>
      </div>
    );
  }

  const renderChart = () => {
    switch (config.chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey={config.xAxis}
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              {config.showLegend && <Legend wrapperStyle={{ fontSize: '12px' }} />}
              {Array.isArray(config.yAxis) ? (
                config.yAxis.map((key, index) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    fill={colors[index % colors.length]}
                    radius={[4, 4, 0, 0]}
                  />
                ))
              ) : (
                <Bar
                  dataKey={config.yAxis || config.valueColumn || dataset.headers[1]}
                  fill={colors[0]}
                  radius={[4, 4, 0, 0]}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey={config.xAxis}
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              {config.showLegend && <Legend wrapperStyle={{ fontSize: '12px' }} />}
              {Array.isArray(config.yAxis) ? (
                config.yAxis.map((key, index) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={colors[index % colors.length]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey={config.yAxis || config.valueColumn || dataset.headers[1]}
                  stroke={colors[0]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey={config.xAxis}
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              {config.showLegend && <Legend wrapperStyle={{ fontSize: '12px' }} />}
              {Array.isArray(config.yAxis) ? (
                config.yAxis.map((key, index) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={colors[index % colors.length]}
                    fill={colors[index % colors.length]}
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                ))
              ) : (
                <Area
                  type="monotone"
                  dataKey={config.yAxis || config.valueColumn || dataset.headers[1]}
                  stroke={colors[0]}
                  fill={colors[0]}
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        const pieDataKey = config.valueColumn || config.yAxis || dataset.headers[1];
        const pieLabelKey = config.labelColumn || config.xAxis || dataset.headers[0];

        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey={typeof pieDataKey === 'string' ? pieDataKey : pieDataKey[0]}
                nameKey={pieLabelKey}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                labelLine={{ stroke: '#666', strokeWidth: 1 }}
              >
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              {config.showLegend && <Legend wrapperStyle={{ fontSize: '12px' }} />}
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-400">Unknown chart type: {config.chartType}</p>
          </div>
        );
    }
  };

  return (
    <div className="chart-block bg-white rounded-lg border border-gray-200 p-4">
      {config.title && (
        <h4 className="text-sm font-semibold text-gray-700 mb-3 text-center">{config.title}</h4>
      )}
      {renderChart()}
    </div>
  );
}
